import React from "react";

import TimePastSvgIcon from "@components/Common/SvgIcons/TimePastSvgIcon";

const lazyNamed = (
    loader: () => Promise<Record<string, React.ComponentType<any>>>,
    name: string
) => React.lazy(() => loader().then((m) => ({ default: m[name] })));

const AdvertisingScreen = lazyNamed(
    () => import("@pages/GeneralConfig/AdvertisingScreen"),
    "AdvertisingScreen"
);
const Design = lazyNamed(() => import("@pages/GeneralConfig/Design"), "Design");
const InformationModes = lazyNamed(
    () => import("@pages/GeneralConfig/InformationModes"),
    "InformationModes"
);
const CustomerAccount = lazyNamed(
    () => import("@pages/GeneralConfig/CustomerAccount"),
    "CustomerAccount"
);
const Languages = lazyNamed(
    () => import("@pages/GeneralConfig/Languages"),
    "Languages"
);
const Options = lazyNamed(
    () => import(/* webpackPrefetch: true */ "@pages/GeneralConfig/Options"),
    "Options"
);
const OrderTaking = lazyNamed(
    () => import("@pages/GeneralConfig/OrderTaking"),
    "OrderTaking"
);
const MeansOfPayment = lazyNamed(
    () => import("@pages/GeneralConfig/MeansOfPayment"),
    "MeansOfPayment"
);
const SalesMethods = lazyNamed(
    () => import("@pages/GeneralConfig/SalesMethods"),
    "SalesMethods"
);
const TemplateSelection = lazyNamed(
    () =>
        import(
            /* webpackPrefetch: true */ "@pages/GeneralConfig/TemplateSelection"
        ),
    "TemplateSelection"
);
const VariousTabs = lazyNamed(
    () => import("@pages/GeneralConfig/Various/VariousTabs"),
    "VariousTabs"
);
const Ticket = lazyNamed(() => import("@pages/GeneralConfig/Ticket"), "Ticket");
const Ways = lazyNamed(() => import("@pages/GeneralConfig/Ways"), "Ways");
const HoursOfUse = lazyNamed(
    () => import("@pages/GeneralConfig/HoursOfUse"),
    "HoursOfUse"
);
const AuthenticationMode = lazyNamed(
    () => import("@pages/GeneralConfig/CustomerAccount/AuthenticationMode"),
    "AuthenticationMode"
);

const HoursOfUseIcon = (
    <TimePastSvgIcon
        height={15}
        width={15}
        fill="white"
        style={{
            marginRight: "10px",
            marginBottom: "3px",
        }}
    />
);

const NOT_CONFIGURED_TABS: TabsMenuType[] = [
    {
        id: "templateSelection",
        titleKey: "Template Selection",
        content: <TemplateSelection />,
        customClassName: "pme_txt_menuTemplateChoice",
    },
    {
        id: "configuration",
        titleKey: "Configuration",
        content: "",
        customClassName: "pme_txt_menuConfiguration",
    },
    {
        id: "ways",
        titleKey: "Ways",
        content: "",
        customClassName: "pme_txt_menuWays",
    },
    {
        id: "advancedSettings",
        titleKey: "Advanced settings",
        content: "",
        customClassName: "pme_txt_menuDesign",
    },
];

const KIOSK_TABS: TabsMenuType[] = [
    {
        id: "configuration",
        titleKey: "Configuration",
        content: "",
        customClassName: "",
        subMenu: [
            {
                id: "options",
                titleKey: "Options",
                content: <Options />,
                customClassName: "",
            },
            {
                id: "advertisingScreen",
                titleKey: "Advertising screen",
                content: <AdvertisingScreen />,
                customClassName: "",
            },
            {
                id: "saleMode",
                titleKey: "Sale mode",
                content: <SalesMethods />,
                customClassName: "",
            },
            {
                id: "meansOfPayment",
                titleKey: "Means of payment",
                content: <MeansOfPayment />,
                customClassName: "",
            },
            {
                id: "informationModes",
                titleKey: "Information modes",
                content: <InformationModes />,
                customClassName: "",
            },
            {
                id: "loyaltyAccount",
                titleKey: "Loyalty account",
                content: <AuthenticationMode />,
                customClassName: "",
            },
            {
                id: "ticket",
                titleKey: "Ticket",
                content: <Ticket />,
                customClassName: "",
            },
            {
                id: "languages",
                titleKey: "Languages",
                content: <Languages />,
                customClassName: "pme_txt_menuLanguages",
            },
        ],
    },
    {
        id: "hoursOfUse",
        titleKey: "Hours of use",
        titleIcon: HoursOfUseIcon,
        content: <HoursOfUse />,
        customClassName: "",
    },
];

const CONFIGURED_TABS: TabsMenuType[] = [
    {
        id: "templateSelection",
        titleKey: "Template Selection",
        content: <TemplateSelection />,
        customClassName: "pme_txt_menuTemplateChoice",
    },
    {
        id: "configuration",
        titleKey: "Configuration",
        content: "",
        customClassName: "pme_txt_menuConfiguration",
        subMenu: [
            {
                id: "options",
                titleKey: "Options",
                content: <Options />,
                customClassName: "pme_txt_menuOptions",
            },
            {
                id: "advertisingScreen",
                titleKey: "Advertising screen",
                content: <AdvertisingScreen />,
                customClassName: "pme_txt_menuAdversitingScreen",
            },
            {
                id: "saleMode",
                titleKey: "Sale mode",
                content: <SalesMethods />,
                customClassName: "pme_txt_menuSaleMode",
            },
            {
                id: "meansOfPayment",
                titleKey: "Means of payment",
                content: <MeansOfPayment />,
                customClassName: "pme_txt_menuPaymentMethod",
            },
            {
                id: "informationModes",
                titleKey: "Information modes",
                content: <InformationModes />,
                customClassName: "pme_txt_menuInformationMode",
            },
            {
                id: "loyaltyAccount",
                titleKey: "Loyalty account",
                content: <CustomerAccount />,
                customClassName: "pme_txt_menuCustomerAccount",
            },
            {
                id: "orderTaking",
                titleKey: "Order taking",
                content: <OrderTaking />,
                customClassName: "pme_txt_menuOrderTaking",
            },
            {
                id: "languages",
                titleKey: "Languages",
                content: <Languages />,
                customClassName: "pme_txt_menuLanguages",
            },
            {
                id: "ticket",
                titleKey: "Dematerialized ticket",
                content: <Ticket />,
                customClassName: "pme_txt_ticket",
            },
            {
                id: "various",
                titleKey: "Various",
                content: <VariousTabs />,
                customClassName: "pme_txt_menuDrivers",
            },
        ],
    },
    {
        id: "ways",
        titleKey: "Ways",
        content: <Ways />,
        customClassName: "pme_txt_menuWays",
    },
    {
        id: "advancedSettings",
        titleKey: "Advanced settings",
        content: <Design />,
        customClassName: "pme_txt_menuDesign",
    },
];

export function buildTabs(
    isConfigured: boolean,
    isKioskSetting: boolean
): TabsMenuType[] {
    if (!isConfigured) return NOT_CONFIGURED_TABS;
    if (isKioskSetting) return KIOSK_TABS;
    return CONFIGURED_TABS;
}
